import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Background } from './components/Background.js';
import { Navbar } from './components/Navbar.js';
import { SalaryPill } from './components/SalaryPill.js';
import { Hub } from './pages/Hub.js';
import { Salary } from './pages/Salary.js';
import { CalculatorPage } from './pages/Calculator.js';
import { Compare } from './pages/Compare.js';
import { Charts } from './pages/Charts.js';
import { Payslip } from './pages/Payslip.js';
import { Methodology } from './pages/Methodology.js';
import { calculators, type Calculator } from './lib/calculators.js';
import { SalaryFormProvider } from './lib/salary-form.js';
import { useTheme } from './lib/theme.js';
import { useBlobs } from './lib/motion.js';

/** Telas que herdam o salário-base e por isso ganham o atalho fixo no lugar de um campo próprio. */
const salaryLinked = (pathname: string) =>
  pathname === '/comparar' || pathname === '/graficos'
  || Object.entries(calculators).some(([slug, entry]) => entry.usesSalaryBase === true && pathname === `/calculos/${slug}`);

function Layout() {
  const location = useLocation();
  const root = useRef<HTMLDivElement>(null);
  const { theme, toggle } = useTheme();
  useBlobs(root);

  // A navbar aponta para /funcionalidades#brandnexx; o router não rola até a âncora sozinho.
  useEffect(() => {
    if (!location.hash) return;
    document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth' });
  }, [location.hash, location.pathname]);

  return (
    <div className="app" ref={root}>
      <Background extraBlob={location.pathname === '/funcionalidades'} />
      <Navbar theme={theme} onToggleTheme={toggle} />
      {salaryLinked(location.pathname) ? <SalaryPill /> : null}
      <AnimatePresence mode="wait">
        <motion.div key={location.pathname} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: .2 }}>
          <Routes location={location}>
            <Route path="/" element={<Navigate to="/funcionalidades" replace />} />
            <Route path="/funcionalidades" element={<Hub />} />
            <Route path="/calculos/salario" element={<Salary />} />
            {Object.keys(calculators).map((slug) => (
              <Route key={slug} path={`/calculos/${slug}`} element={<CalculatorPage kind={slug as Calculator} />} />
            ))}
            <Route path="/comparar" element={<Compare />} />
            <Route path="/graficos" element={<Charts />} />
            <Route path="/holerite" element={<Payslip />} />
            <Route path="/metodologia" element={<Methodology />} />
            <Route path="*" element={<Navigate to="/funcionalidades" replace />} />
          </Routes>
        </motion.div>
      </AnimatePresence>
      <footer className="site-footer">Resultados estimativos. Revise as premissas antes de decidir.</footer>
    </div>
  );
}

export function App() {
  return <BrowserRouter><SalaryFormProvider><Layout /></SalaryFormProvider></BrowserRouter>;
}
